import React from 'react';
import CircleShape from '../components/shapes/Circle'
import SquareShape from '../components/shapes/Square'
import TriangleShape from '../components/shapes/Triangle'
export function generateGradientDef(layerIndex, colorData, radius, fixedCanvasSize, isLegend=false) {
    const gradientId = `gradient-${layerIndex}${isLegend?"0":""}`;
    const center = fixedCanvasSize / 2;
    const stops = (
      <>
        <stop offset={colorData.stop1 || '0%'} stopColor={colorData.color1} stopOpacity="1" />
        <stop offset={colorData.stop2 || '100%'} stopColor={colorData.color2} stopOpacity="1" />
      </>
    );

    switch (colorData.gradientType) {
      case 'radial':
        return (
          <radialGradient
            id={gradientId}
            cx={center}
            cy={center}
            r={radius}
            fx={center}
            fy={center}
            gradientUnits="userSpaceOnUse"
          >
            {stops}
          </radialGradient>
        );
      case 'diamond':
        return (
          <linearGradient
            id={gradientId}
            x1={center - radius}
            y1={center - radius}
            x2={center + radius}
            y2={center + radius}
            gradientUnits="userSpaceOnUse"
          >
            {stops}
          </linearGradient>
        );
      case 'linear':
      default: {
        const { x1, y1, x2, y2 } = degToCoords(colorData.degree, center, radius);
        return (
          <linearGradient
            id={gradientId}
            x1={x1}
            y1={y1}
            x2={x2}
            y2={y2}
            gradientUnits="userSpaceOnUse"
          >
            {stops}
          </linearGradient>
        );
      }
    }
}
 const degToCoords = (degreeStr, center, radius) => {
    const deg = parseFloat(degreeStr?.replace('deg', '') || '0');
    const rad = (deg * Math.PI) / 180;
    const dx = Math.cos(rad) * radius;
    const dy = Math.sin(rad) * radius;

    return {
      x1: center - dx,
      y1: center - dy,
      x2: center + dx,
      y2: center + dy,
    };
  };
export function getRadiusForLayer(layer, fixedCanvasSize, zoom) {
  const divideNum =
    layer.shapeType === 'circle' ? 4.1 :
    layer.shapeType === 'square' ? 4.6 :
    layer.shapeType === 'triangle' ? 3.6 :
    layer.shapeType === 'hexagon' ? 4.1 :
    4.1;

  return (layer.shapeRadius / 100) * (fixedCanvasSize / divideNum) * zoom / 100;
}

export function rotatePoint(x, y, angleDeg){
    const rad = (angleDeg * Math.PI) / 180;
    return {
      x: x * Math.cos(rad) - y * Math.sin(rad),
      y: x * Math.sin(rad) + y * Math.cos(rad),
    };
  };
export function generateShapes (layer, zoom, fixedCanvasSize, isLegend=false, isHomepage=false) {
  const {
    numShapes,
    shapeRadius: shapeRadiusPercent,
    shapeType,
    opacity,
  } = layer;

  let divideNum = 1;
  let multiplyer = isHomepage ? 1.25 : 1.1;
  let legendMultiplyer = 1;

  const shapes = [];
  const angleIncrement = (2 * Math.PI) / numShapes;
  const strokeSize = Number(layer.strokeWidth)  ?? 1;
  let strokeWidth = numShapes > 99 ? strokeSize*0.1 : strokeSize*1;
  if(isLegend) strokeWidth = numShapes < 32? 0.5 : 0.01;
  const isSingle = numShapes == 1;
  const centerX = fixedCanvasSize / 2;
  const centerY = fixedCanvasSize / 2;

  if(!isLegend){
      if (shapeType === 'circle') divideNum = 5.1;
      else if (shapeType === 'square') divideNum = 5.7;
      else if (shapeType === 'triangle') divideNum = 7.6;
      else if (shapeType === 'hexagon') divideNum = 5.1;
      else if (shapeType === 'hypo') divideNum = 5.1;
  }else{
    if (shapeType === 'circle') legendMultiplyer = 1;
      else if (shapeType === 'square') legendMultiplyer = 1;
      else if (shapeType === 'triangle') legendMultiplyer = isSingle ? 0.9 : 0.77;
      else if (shapeType === 'hexagon') legendMultiplyer = 1.2
      else if (shapeType === 'hypo') legendMultiplyer = 1
  }

  const radius = isLegend? 7*legendMultiplyer : (zoom/100) * (shapeRadiusPercent / 100) * fixedCanvasSize / divideNum * multiplyer;

  // ======== NEW: force a start angle so first shape rotation starts from 0 deg ========
  // Set to 0 so first shape has rotation = 0.
  // If you want the first shape positioned at the top, use -90 here.
  const startAngleDeg = 0; 
  const startAngleRad = (startAngleDeg * Math.PI) / 180;
  // ================================================================================

  for (let i = 0; i < numShapes; i++) {
    // add startAngleRad so the first shape is at startAngleRad rather than implicit 0
    const angle = startAngleRad + i * angleIncrement;
    // add startAngleDeg for the per-shape rotation so first rotation is 0 (or whatever you set)
    const rotation = startAngleDeg + (360 / numShapes) * i;

    const baseX = isSingle ? centerX : centerX + radius * Math.cos(angle);
    const baseY = isSingle ? centerY : centerY + radius * Math.sin(angle);

    let pathData = '';
    let shapeElement = null;

    if (shapeType === 'circle') {
      const rad = isSingle? radius*1.9 : radius;
      shapeElement = (
        <CircleShape
          baseX={baseX}
          baseY={baseY}
          radius={rad}
          strokeWidth={strokeWidth}
          opacity={opacity}
        />
      );
    } else if (shapeType === 'square') {
      const size = isSingle ? radius * 4 : radius * 2;
      const half = size / 2;
      const corners = [
        rotatePoint(-half, -half, rotation),
        rotatePoint(half, -half, rotation),
        rotatePoint(half, half, rotation),
        rotatePoint(-half, half, rotation),
      ];
      pathData = SquareShape(baseX, baseY, corners);
    } else if (shapeType === 'triangle') {
      const halfBase = isSingle ? Math.max(1, radius) * 2.5 : Math.max(1, radius)*2;
      const height = halfBase * Math.sqrt(3);

      const anchorX = baseX;
      const anchorY = isSingle ? baseY-halfBase/3.5: baseY;

      const relPoints = isSingle ? [
        { x: -halfBase, y: height / 2 },
        { x: halfBase, y: height / 2 },
        { x: 0, y: -height / 2 },
      ]
      :
      [
        { x: -halfBase/2, y: height / 2 },
        { x: halfBase, y: 0 },
        { x: -halfBase/2, y: -height / 2 },
      ];

      const rotated = relPoints.map(p => rotatePoint(p.x, p.y, rotation));

      pathData = TriangleShape(anchorX, anchorY, rotated);

    } else if (shapeType === 'hexagon') {
      const size = isSingle ? radius * 2 : radius ;
      const rawPoints = Array.from({ length: 6 })
        .map((_, j) => {
          const angleOffset = (360 / 6) * j;
          const { x, y } = rotatePoint(
            size * Math.cos((angleOffset * Math.PI) / 180),
            size * Math.sin((angleOffset * Math.PI) / 180),
            rotation
          );
          return `${j === 0 ? 'M' : 'L'} ${baseX + x},${baseY + y}`;
        });
      pathData = rawPoints.join(' ') + ' Z';
    }

    if (pathData) {
      shapes.push(
        <path
          key={i}
          d={pathData}
          fill="none"
          strokeWidth={strokeWidth}
          opacity={opacity / 100}
        />
      );
    } else if (shapeElement) {
      shapes.push(React.cloneElement(shapeElement, { key: i }));
    }
  }

  return shapes;
}
